import React, { useEffect, useMemo, useState } from "react";
import "./../../../styles/SupportDashboard.css";
import Button from './../../ui/Button';

export default function ResetPasswordModal({ isOpen, onClose, user, onSubmit }) {
  const [username, setUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [err, setErr] = useState({});
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setUsername(user?.username || "");
    setNewPassword("");
    setErr({});
    setSuccess("");
    setSaving(false);
    setShowPw(false);
  }, [isOpen, user]);

  const canSubmit = useMemo(() => {
    return !!username.trim() && !!newPassword && !saving;
  }, [username, newPassword, saving]);

  const validate = () => {
    const e = {};
    if (!username.trim()) e.username = "Username is required.";
    if (!newPassword) e.newPassword = "New password is required.";
    else if (newPassword.length < 12) e.newPassword = "Use at least 12 characters.";
    setErr(e);
    return Object.keys(e).length === 0;
  };


  const submit = async () => {
    if (!validate()) return;

    setSaving(true);
    setErr({});
    setSuccess("");

    try {
      await onSubmit({
        username: username.trim(),
        newPassword,
      });

      setSuccess(`Password has been reset for ${username.trim()}.`);

      setTimeout(() => onClose(), 3200);
    } catch (ex) {
      const api = ex?.body ?? ex?.response?.data ?? null;
      const errors = Array.isArray(api?.errors) ? api.errors : [];

      const messages = errors.map((e) => e?.message).filter(Boolean);
      const apiMessage =
        messages[0] || api?.message || ex?.message || "Ensure your old password is correct and your new one meets the requirements.";

      const fieldErrors = {};
      for (const e of errors) {
        const msg = (e?.message || "").toLowerCase();

        if (e?.target) {
          fieldErrors[e.target] = e.message;
          continue;
        }

        if (msg.includes("username") || msg.includes("user")) fieldErrors.username = e.message;
        if (msg.includes("password")) fieldErrors.newPassword = e.message;
      }

      setErr((prev) => ({
        ...prev,
        ...fieldErrors,
        api: messages.length ? messages.join(" • ") : apiMessage,
      }));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="sd-modal-backdrop" role="dialog" aria-modal="true">
      <div className="sd-modal sd-modal-md">
        <div className="sd-modal-header">
          <div>
            <h2 className="sd-title">Reset Password for Android Accounts</h2>
            <div className="sd-subtitle">Police, Super & Admin user accounts</div>
          </div>
          <button className="sd-icon-btn" onClick={onClose} aria-label="Close" type="button">
            ✕
          </button>
        </div>

        <div className="sd-modal-body">
          {err.api && <div className="sd-alert sd-alert-bad">{err.api}</div>}
          {success && <div className="sd-alert sd-alert-good">{success}</div>}

          <div className="sd-field">
            <div className="sd-label">Username</div>
            <input
              className={`sd-input ${err.username ? "has-error" : ""}`}
              value={username}
              onChange={(e) => setUsername(e.target.value)}              
              autoComplete="off"
              disabled={!!user?.username}
            />
            {err.username && <div className="sd-error">{err.username}</div>}
          </div>

          <div className="sd-field">
            <div className="sd-label">New password</div>
            <div className="sd-inline">
              <input
                className={`sd-input ${err.newPassword ? "has-error" : ""}`}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                type={showPw ? "text" : "password"}
                placeholder="Enter a strong password…"
                autoComplete="new-password"
              />
              <Button
                buttonStyle="btn-alternate"
                onClick={() => setShowPw((v) => !v)}
              >
                {showPw ? "Hide" : "Show"}
              </Button>
            </div>
            {err.newPassword && <div className="sd-error">{err.newPassword}</div>}
            <div className="sd-help">Tip: use a 3-words generator e.g. “correctHorseBattery7#”.</div>
          </div>
        </div>

        <div className="sd-modal-footer">
          <Button onClick={onClose} disabled={saving} buttonStyle="btn-alternate">
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit} buttonStyle="btn-main" type="button">
            {saving ? "Resetting…" : "Reset password"}
          </Button>
        </div>
      </div>
    </div>
  );
}
